/**
 * The operator-facing copy module: one import path for every word the product says.
 *
 * `import { TERMS, plural } from '@/lib/copy'` - never from `./terms` directly, so the later
 * Hebrew work (#505 T9) has exactly one module to swap and one extractor path to walk.
 *
 * WHAT LIVES HERE, and what does not.
 *
 * A string moves into this module when any of these is true:
 *   1. it belongs to a closed set - an enum-to-label map, a status vocabulary, a command key,
 *      a nav entry;
 *   2. it appears in more than one file, or names a concept that does;
 *   3. a test asserts it, or should;
 *   4. it contains a `TERMS` word as a standalone noun, so a vocabulary reversal is mechanical.
 *
 * Everything else stays next to the component that renders it. Body prose read once in one
 * place is more reviewable where it is than hauled into a three-thousand-line dictionary, and
 * the part that must not drift - the vocabulary - is the part that moved.
 *
 * Two shape rules, because they are cheap now and expensive later:
 *   - an interpolated string is an exported function with NAMED parameters, never a template
 *     literal at the call site;
 *   - a sentence is never assembled by concatenating JSX fragments.
 *
 * The section files (`status`, `dock`, `commands`, `mission`, `nav`, `chrome`, `errors`) land
 * with the surfaces they serve. This file gains a re-export as each one arrives.
 */

export {
  TERMS,
  RETIRED_TERMS,
  ABBREVIATIONS,
  PROPER_NOUNS,
  plural,
  pluralWord,
} from './terms';
export type { Term } from './terms';

export {
  STATUS_LABEL,
  CHIP_STATE_LABEL,
  EXCEPTION_COPY,
  CRITICAL_EXCEPTIONS,
} from './status';
export type { StatusKey, ChipStateKey } from './status';

export {
  DOCK_STATUS_LABEL,
  DOCK_READY_BASIS_LABEL,
  UNBOUNDED_DOCK_READY_BASES,
  DOCK_STATUS_SHORT,
  DOCK_PHASE_LABEL,
  DOCK_PHASE_UNKNOWN,
  BAY_POSITION_LABEL,
  bayPositionUnknown,
  PRESENCE_LABEL,
  AIRBORNE_LABEL,
  DOCK_CAPABILITY,
  DOCK_SLOT_LABEL,
  CRITICAL_DOCK_LABELS,
  DOCK_POSITION_COPY,
  DOCK_PIN_EDITOR_COPY,
} from './dock';

export {
  COMMAND_KEY_LABEL,
  SHOUTING_COMMAND_KEYS,
  DIRECT_VARIANT,
  CONFIRM_COPY,
  CONFIRM_DIALOG,
  HELD_ARM_PROMPT,
  DOCK_COMMAND_LABEL,
  DOCK_COMMAND_EFFECT,
  DOCK_PHASE_ROW,
} from './commands';

export {
  MISSION_TYPE_LABEL,
  MISSION_TYPE_DESCRIPTION,
  MISSION_TYPE_UNFLYABLE,
  MISSION_TYPE_UNAVAILABLE,
  MISSION_TYPE_UNAVAILABLE_FALLBACK,
  COVERAGE_ROW_LABEL,
  PAST_MISSIONS_TITLE,
  MISSION_ROW_FALLBACK,
  MISSION_COPY_ACTION,
  aMissionType,
} from './mission';

export {
  NAV_GROUP_LABEL,
  NAV_ITEM_LABEL,
  AIR_PICTURE_TITLE,
  SETUP_TITLE,
  SYSTEM_HEALTH_LABEL,
  CRITICAL_SYSTEM_HEALTH,
  DEPENDENCY_LABEL,
  DEPENDENCY_STATE_LABEL,
  dependencyTooltip,
  siteCount,
  DISPLAY_MODE,
} from './nav';
export type { NavGroupKey, SystemHealthKey, DependencyKey, DependencyStateKey } from './nav';

export {
  MAP_LEGEND,
  MAP_CONTROL,
  MAP_LAYERS_MENU,
  DOCK_ROSTER,
  MAP_CONTEXT_MENU,
  SHEET_TAB,
  ALERT_HUD,
  SEVERITY_LABEL,
  CRITICAL_SEVERITIES,
  SITE_FILTER,
  STRIP_BOARD,
  WORKSPACE_SEGMENT,
} from './chrome';
export type { SeverityKey } from './chrome';
